/**
 * Storage node API client (Protocol v1)
 */

import { getAccessToken, getValidAccessToken, refreshSessionToken } from './authClient.js';
import { STORAGE_API_URL, RELAY_API_URL } from './config.js';
import { syncCircuitBreaker, globalStorageRateLimiter } from '@yada/sync';
import { HttpNodeTransport, StorageNodeError, type ManifestDoc, type DocumentRead, type SnapshotWrite, type NodeTransport } from '@yada/storage-protocol';

export { syncCircuitBreaker, StorageNodeError };

export class StorageError extends Error {
  constructor(public status: number, message: string, public errorData?: any) {
    super(message);
    this.name = 'StorageError';
  }
}

/**
 * Get the storage node URL from localStorage preference or env/default
 */
export function getStorageUrl(): string {
  if (typeof localStorage !== 'undefined') {
    const stored = localStorage.getItem('yada:defaultStorageUrl');
    const isLocalHost = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
    if (stored) {
      if (!isLocalHost && (stored.includes('localhost') || stored.includes('127.0.0.1'))) {
        localStorage.setItem('yada:defaultStorageUrl', STORAGE_API_URL);
        return STORAGE_API_URL;
      }
      if (typeof window !== 'undefined' && window.location.protocol === 'https:' && stored.startsWith('http://')) {
        const upgraded = stored.replace(/^http:\/\//, 'https://');
        localStorage.setItem('yada:defaultStorageUrl', upgraded);
        return upgraded;
      }
      return stored;
    }
  }
  return STORAGE_API_URL;
}

/**
 * Get the realtime relay URL from localStorage preference or env/default
 */
export function getRelayUrl(): string {
  if (typeof localStorage !== 'undefined') {
    const stored = localStorage.getItem('yada:defaultRelayUrl');
    const isLocalHost = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
    if (stored) {
      if (!isLocalHost && (stored.includes('localhost') || stored.includes('127.0.0.1'))) {
        return getStorageUrl();
      }
      if (typeof window !== 'undefined' && window.location.protocol === 'https:' && stored.startsWith('http://')) {
        const upgraded = stored.replace(/^http:\/\//, 'https://');
        localStorage.setItem('yada:defaultRelayUrl', upgraded);
        return upgraded;
      }
      return stored;
    }
  }
  const isLocalHost = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  if (!isLocalHost && (RELAY_API_URL.includes('localhost') || RELAY_API_URL.includes('127.0.0.1'))) {
    return getStorageUrl();
  }
  return RELAY_API_URL || getStorageUrl();
}

let _customStorageTransport: NodeTransport | null = null;

/**
 * Configure or override the default storage transport (useful for tests or custom backends).
 */
export function setStorageTransport(transport: NodeTransport | null): void {
  _customStorageTransport = transport;
}

/**
 * Returns a NodeTransport instance configured for the storage node.
 */
export function getStorageTransport(baseUrl?: string): NodeTransport {
  if (_customStorageTransport && !baseUrl) {
    return _customStorageTransport;
  }
  const url = baseUrl || getStorageUrl();
  return new HttpNodeTransport({
    baseUrl: url,
    token: () => getValidAccessToken() ?? getAccessToken(),
    refreshToken: () => refreshSessionToken(),
  });
}

/**
 * Get document manifest for a workspace.
 */
export async function getDocumentManifest(
  workspaceId: string,
  after?: string,
  since?: string
): Promise<{ cursor: string; documents: ManifestDoc[] }> {
  await globalStorageRateLimiter.acquire(1);
  return syncCircuitBreaker.execute(async () => {
    const transport = getStorageTransport();
    try {
      const res = await transport.manifest(workspaceId, after, since);
      return { cursor: res.cursor, documents: res.docs };
    } catch (err: unknown) {
      if (err instanceof StorageNodeError) {
        throw new StorageError(err.status, err.message, err.details);
      }
      throw err;
    }
  }, `manifest-${workspaceId}`);
}

/**
 * Check if a document exists on the storage node using a cheap HEAD request.
 */
export async function hasDocument(
  workspaceId: string,
  documentId: string
): Promise<boolean> {
  const transport = getStorageTransport();
  if (typeof transport.head === 'function') {
    return transport.head(workspaceId, documentId);
  }
  try {
    await getDocument(workspaceId, documentId);
    return true;
  } catch {
    return false;
  }
}

/**
 * Read document with snapshots and updates.
 */
export async function getDocument(
  workspaceId: string,
  documentId: string,
  since?: string
): Promise<DocumentRead> {
  await globalStorageRateLimiter.acquire(1);
  return syncCircuitBreaker.execute(async () => {
    const transport = getStorageTransport();
    try {
      return await transport.read(workspaceId, documentId, since);
    } catch (err: unknown) {
      if (err instanceof StorageNodeError) {
        throw new StorageError(err.status, err.message, err.details);
      }
      throw err;
    }
  }, documentId);
}

/**
 * Read up to 200 documents per batch request.
 */
export async function getDocumentsBatch(
  workspaceId: string,
  documentQueries: (string | import('@yada/storage-protocol').BatchDocumentQuery)[]
): Promise<DocumentRead[]> {
  if (documentQueries.length === 0) return [];

  // Deduplicate by ID (preserving query with since if present)
  const queryMap = new Map<string, string | import('@yada/storage-protocol').BatchDocumentQuery>();
  for (const q of documentQueries) {
    const id = typeof q === 'string' ? q : q.id;
    if (!queryMap.has(id) || typeof q === 'object') {
      queryMap.set(id, q);
    }
  }
  const uniqueQueries = Array.from(queryMap.values());
  const CHUNK_SIZE = 50;

  if (uniqueQueries.length <= CHUNK_SIZE) {
    await globalStorageRateLimiter.acquire(1);
    return syncCircuitBreaker.execute(async () => {
      const transport = getStorageTransport();
      try {
        return await transport.readBatch(workspaceId, uniqueQueries);
      } catch (err: unknown) {
        if (err instanceof StorageNodeError) {
          throw new StorageError(err.status, err.message, err.details);
        }
        throw err;
      }
    }, `batch-${workspaceId}`);
  }

  const chunks: (string | import('@yada/storage-protocol').BatchDocumentQuery)[][] = [];
  for (let i = 0; i < uniqueQueries.length; i += CHUNK_SIZE) {
    chunks.push(uniqueQueries.slice(i, i + CHUNK_SIZE));
  }

  const chunkResults = await Promise.all(
    chunks.map((chunk) => getDocumentsBatch(workspaceId, chunk))
  );

  return chunkResults.flat();
}

/**
 * Append CRDT update (writer, counter, blob).
 */
export async function appendUpdate(
  workspaceId: string,
  documentId: string,
  writer: string,
  counter: number,
  blob: Uint8Array | string
): Promise<{ cursor: string }> {
  await globalStorageRateLimiter.acquire(1);
  return syncCircuitBreaker.execute(async () => {
    const transport = getStorageTransport();
    try {
      return await transport.putUpdate(workspaceId, documentId, writer, counter, blob);
    } catch (err: unknown) {
      if (err instanceof StorageNodeError) {
        throw new StorageError(err.status, err.message, err.details);
      }
      throw err;
    }
  }, documentId);
}

/**
 * Put snapshot (compaction).
 */
export async function putSnapshot(
  workspaceId: string,
  documentId: string,
  snapshot: SnapshotWrite
): Promise<{ cursor: string }> {
  await globalStorageRateLimiter.acquire(1);
  return syncCircuitBreaker.execute(async () => {
    const transport = getStorageTransport();
    try {
      return await transport.putSnapshot(workspaceId, documentId, snapshot);
    } catch (err: unknown) {
      if (err instanceof StorageNodeError) {
        throw new StorageError(err.status, err.message, err.details);
      }
      throw err;
    }
  }, documentId);
}

/**
 * Delete document (tombstone).
 */
export async function deleteDocument(
  workspaceId: string,
  documentId: string
): Promise<{ cursor: string }> {
  await globalStorageRateLimiter.acquire(1);
  return syncCircuitBreaker.execute(async () => {
    const transport = getStorageTransport();
    try {
      return await transport.tombstone(workspaceId, documentId);
    } catch (err: unknown) {
      if (err instanceof StorageNodeError) {
        throw new StorageError(err.status, err.message, err.details);
      }
      throw err;
    }
  }, documentId);
}

/**
 * Upload an encrypted asset to storage node.
 */
export async function uploadAsset(
  workspaceId: string,
  assetId: string,
  data: Uint8Array,
  mimeType?: string
): Promise<void> {
  await globalStorageRateLimiter.acquire(1);
  return syncCircuitBreaker.execute(async () => {
    const transport = getStorageTransport();
    try {
      if (typeof transport.putAsset === 'function') {
        return await transport.putAsset(workspaceId, assetId, data, mimeType);
      }
      throw new Error('Storage transport does not support asset uploads');
    } catch (err: unknown) {
      if (err instanceof StorageNodeError) {
        throw new StorageError(err.status, err.message, err.details);
      }
      throw err;
    }
  }, `asset-${assetId}`);
}

/**
 * Fetch an encrypted asset from storage node.
 */
export async function fetchAsset(
  workspaceId: string,
  assetId: string
): Promise<{ data: Uint8Array; mimeType: string }> {
  await globalStorageRateLimiter.acquire(1);
  return syncCircuitBreaker.execute(async () => {
    const transport = getStorageTransport();
    try {
      if (typeof transport.getAsset === 'function') {
        return await transport.getAsset(workspaceId, assetId);
      }
      throw new Error('Storage transport does not support asset downloads');
    } catch (err: unknown) {
      if (err instanceof StorageNodeError) {
        throw new StorageError(err.status, err.message, err.details);
      }
      throw err;
    }
  }, `asset-${assetId}`);
}

/**
 * Register a pairing code on the relay server.
 */
export async function registerPairingCode(
  code: string,
  sessionId: string,
  publicKeyBase64: string,
  deviceId?: string
): Promise<void> {
  const relayUrl = getRelayUrl();
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('yada:access_token') : null;
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const response = await fetch(`${relayUrl}/api/signal/pair-codes`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ code, sessionId, ephemeralPublicKey: publicKeyBase64, deviceId }),
  });
  if (!response.ok) {
    throw new StorageError(response.status, `Failed to register pairing code: ${response.status}`);
  }
}

/**
 * Look up a pairing code entered by the user.
 */
export async function lookupPairingCode(
  code: string
): Promise<{ sessionId: string; ephemeralPublicKey: string; targetDeviceId?: string }> {
  const relayUrl = getRelayUrl();
  const normalised = code.trim().toLowerCase().replace(/\s+/g, ' ');
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('yada:access_token') : null;
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const response = await fetch(
    `${relayUrl}/api/signal/pair-codes/${encodeURIComponent(normalised)}`,
    { headers }
  );
  if (response.status === 404) {
    throw new StorageError(404, 'Pairing code not found or expired. Ask the other device to generate a new one.');
  }
  if (!response.ok) {
    throw new StorageError(response.status, `Failed to look up pairing code: ${response.status}`);
  }
  const data = await response.json();
  return {
    sessionId: data.sessionId,
    ephemeralPublicKey: data.ephemeralPublicKey,
    targetDeviceId: data.deviceId || data.targetDeviceId,
  };
}
